// Angular
import { Injectable } from '@angular/core';
// Store
import { Store } from '@ngxs/store';
// Service
import { JPathService } from '@cv-invision/cv.component-library.ux';
import { ViewsService } from '../../services/views.service';
// Model
import { PROPERTY_TYPE, ROOM_POSITION } from '../models/property-const.model';
import { LocationModel } from '../cv-layout-render/components/common/map-location/model/location.model';
import { PhotoModel } from '../cv-layout-render/components/common/map-location/model/photo.model';
import { SCHEMA_TYPE } from '../../models/enums.model';

@Injectable({ providedIn: 'root' })
export class LocationPhotoUtilsService {

	private currentSchemaPathReviewed = '';
	private currentEnumerationReviewed: any = {};

	get order(): any {
		return this.viewsServices.order;
	}

	get document(): any {
		return this.viewsServices.document;
	}

	get schema(): any {
		return this.viewsServices.schema;
	}

	get rooms(): any {
		return this.viewsServices.rooms;
	}

	constructor(
		private store: Store,
		private viewsServices: ViewsService,
		private jPathService: JPathService
	) {
		//
	}

	/**
	 * Get from the raw current Photos, the object required for the Overview Model
	 * @param photo
	 */
	public getExteriorPhotoInfo(photoModel: PhotoModel): LocationModel {
		const document = this.document;
		const schema = this.schema;

		let property = null;

		switch (this.order.schema) {
			case SCHEMA_TYPE.CVDS_34:
			case SCHEMA_TYPE.CVDS_40:
			case SCHEMA_TYPE.INSPECTIONLESS_1:
				property = this.rooms.exterior.find((room: any) => room._Path === photoModel.refJsonPath);
				break;
		}

		if (property && property.position === ROOM_POSITION.EXTERIOR) {
			const enumType = this.getValueType(photoModel, document, property, schema);

			return {
				id: photoModel.id,
				property: {
					index: this.getPhotoIndex(photoModel.refJsonPath),
					type: photoModel.metadata.type,
					enumType: enumType
				},
				position: {
					lat: photoModel.metadata.latitude,
					lng: photoModel.metadata.longitude
				},
				description: this.getDescription(photoModel, enumType, property),
				imagePath: photoModel.path,
				thumbnailPath: photoModel.thumbnails[0],
				accuracy: photoModel.metadata.accuracy,
				distanceAccuracy: photoModel.distanceAccuracy,
				iconUrl: this.getIconURL(PROPERTY_TYPE.EXTERIOR),
				isInfoWindowOpen: false,
				refJsonPath: photoModel.refJsonPath
			};
		}

		return null;
	}

	/**
	 * Get all Smart Tag from the Property that are type Location to render also in the Map
	 */
	public getSmartTagForPropertyLocations(smartTags: any[]): LocationModel[] {

		let loadSmartTags = false;
		const smartTagLocations: LocationModel[] = [];

		switch (this.order.schema) {
			case SCHEMA_TYPE.CVDS_40:
			case SCHEMA_TYPE.INSPECTIONLESS_1:
				loadSmartTags = true;
				break;
		}

		if (loadSmartTags && smartTags && smartTags.length > 0) {
			smartTags.forEach((smartTag: any) => {
				const smartTagPhoto = this.viewsServices.photos.find((photo) => photo.refJsonPath === smartTag._Path);
				if (smartTagPhoto) {
					// const enumType = this.getValueType(smartTagPhoto, document, property, schema);
					smartTagLocations.push({
						id: smartTagPhoto.id,
						property: {
							index: this.getPhotoIndex(smartTagPhoto.refJsonPath),
							type: smartTagPhoto.metadata.type,
							enumType: {
								Key: 'SMART_TAG_LOCATION',
								Type: 'SmartTag Location'
							}
						},
						position: {
							lat: smartTagPhoto.metadata.latitude,
							lng: smartTagPhoto.metadata.longitude
						},
						description: smartTagPhoto.metadata.description,
						imagePath: smartTagPhoto.path,
						thumbnailPath: smartTagPhoto.thumbnails[0],
						accuracy: smartTagPhoto.metadata.accuracy,
						distanceAccuracy: smartTagPhoto.distanceAccuracy,
						iconUrl: this.getIconURL(PROPERTY_TYPE.LOCATION),
						isInfoWindowOpen: false,
						refJsonPath: smartTagPhoto.refJsonPath,
						subCategory: smartTag.subCategory
					});
				}
			});

		}

		return smartTagLocations;
	}

	/**
	 * Get Current CV Location
	 * @param photo
	 */
	public getCVLocation(cvPropertyLocation: { latitude: any, longitude: any }): LocationModel {
		if (cvPropertyLocation && cvPropertyLocation.latitude !== null && cvPropertyLocation.latitude !== '') {
			return {
				id: '2',
				property: {
					enumType: {
						Key: 'CV',
						Type: 'Location'
					}
				},
				position: {
					lat: cvPropertyLocation.latitude,
					lng: cvPropertyLocation.longitude
				},
				description: 'Main Address registered by CV',
			};
		}
		return null;
	}

	/**
	 * Get Current Property Location
	 * @param photo
	 */
	public getPropertyLocation(propertyLocation: { latitude: any, longitude: any }): LocationModel {
		if (propertyLocation && propertyLocation.latitude !== null && propertyLocation.latitude !== '') {
			return {
				id: '1',
				property: {
					enumType: {
						Key: 'PROPERTY',
						Type: 'Location'
					}
				},
				position: {
					lat: propertyLocation.latitude,
					lng: propertyLocation.longitude
				},
				description: 'Address Property Location',
			};
		}
		return null;
	}

	/**
	 * Get the Marker based on the type
	 * @param photoMarker
	 */
	private getIconURL(metadataType: PROPERTY_TYPE): string {
		if (metadataType === PROPERTY_TYPE.ROOM) {
			return 'assets/img/marker/marker-room-active.png';
		}
		if (metadataType === PROPERTY_TYPE.FEATURE) {
			return 'assets/img/marker/marker-feature-active.png';
		}
		if (metadataType === PROPERTY_TYPE.UPDATE) {
			return 'assets/img/marker/marker-update-active.png';
		}
		if (metadataType === PROPERTY_TYPE.EXTERIOR) {
			return 'assets/img/marker/marker-exterior-location.png';
		}
		if (metadataType === PROPERTY_TYPE.LOCATION) {
			return 'assets/img/marker/marker-smart-tag-location.png';
		}
		return '';
	}

	/**
	 * Get the proper description or Other Description
	 * @param photoModel
	 * @param document
	 * @param property
	 */
	private getValueType(
		photoModel: PhotoModel,
		document: any,
		property: any,
		schema: any
	): any {
		let typeValueEnumeration = { Key: '', Value: '', Type: '' };
		if (photoModel.metadata.type === PROPERTY_TYPE.ROOM || photoModel.metadata.type === PROPERTY_TYPE.OTHER) {
			const schemaPath = this.getSchemaPath(photoModel.refJsonPath);
			if (schemaPath !== this.currentSchemaPathReviewed) {
				this.currentSchemaPathReviewed = schemaPath;
				this.currentEnumerationReviewed = this.jPathService.getValue(
					schema,
					`$..${schemaPath}.roomType.Metadata[0].Enumeration`
				);
			}

			if (photoModel.metadata.type === PROPERTY_TYPE.ROOM && this.currentEnumerationReviewed) {
				typeValueEnumeration = Object.assign({}, this.currentEnumerationReviewed.find(enumType => enumType.Key === property.roomType));
			} else if (photoModel.metadata.type === PROPERTY_TYPE.ROOM || photoModel.metadata.type === PROPERTY_TYPE.OTHER) {
				if (photoModel.metadata && photoModel.metadata.description) {
					typeValueEnumeration.Value = photoModel.metadata.description;
				}
				if (photoModel.metadata.type === PROPERTY_TYPE.OTHER) {
					typeValueEnumeration.Key = PROPERTY_TYPE.OTHER;
				}
			}
			// Special case for the Other Room
			if (typeValueEnumeration.Key === PROPERTY_TYPE.OTHER_ROOM) {
				typeValueEnumeration.Type = 'Other Room';
			} else {
				typeValueEnumeration.Type = this.capitalizeName(photoModel.metadata.type);
			}
		}
		if (photoModel.metadata.type === PROPERTY_TYPE.UPDATE) {
			typeValueEnumeration = { Key: '', Value: '', Type: 'Update' };
		}
		if (photoModel.metadata.type === PROPERTY_TYPE.FEATURE) {
			typeValueEnumeration = { Key: '', Value: '', Type: 'Feature' };
		}

		if ([PROPERTY_TYPE.FEATURE, PROPERTY_TYPE.UPDATE, PROPERTY_TYPE.ROOM, PROPERTY_TYPE.OTHER].indexOf(photoModel.metadata.type) === -1) {
			// a.k.a, we dont have mapped the photoType
			typeValueEnumeration = {
				Key: photoModel.metadata.type,
				Value: photoModel.metadata.description,
				Type: 'Other'
			};
		}

		return typeValueEnumeration;
	}

	/**
	 * Get the proper description or Other Description
	 * @param photoModel
	 * @param document
	 * @param property
	 */
	private getDescription(
		photoModel: PhotoModel,
		enumType: any,
		property: any
	): string {
		if (enumType.Key === PROPERTY_TYPE.OTHER_ROOM) {
			return property.otherDescription;
		}
		if (photoModel.metadata.type === PROPERTY_TYPE.ROOM || enumType.Key === PROPERTY_TYPE.OTHER) {
			return enumType.Value;
		}

		if (photoModel.metadata.type === PROPERTY_TYPE.UPDATE || photoModel.metadata.type === PROPERTY_TYPE.FEATURE) {
			return (property && property.description) ? property.description : '';
		}

		return '';
	}

	/**
	 * Return the schema path without index
	 * @param path
	 */
	getSchemaPath(path: string): string {
		const pathPattern = /(\[[a-zA-z=0-9'\\]+\])/g;
		const schemaPath = path.replace(pathPattern, '');

		return schemaPath;
	}

	/**
	 * Get just Photo Index
	 * @param refJsonPath
	 */
	public getPhotoIndex(refJsonPath: string): number {
		const index = refJsonPath.match(/(\d+)/g);
		if (index) {
			return parseInt(index.join(''), 10);
		}
		return null;
	}

	/**
	 * Return the name Capitalized
	 * @param name
	 */
	private capitalizeName(name: string): string {
		try {
			const nameLowered = name.toLocaleLowerCase();
			return nameLowered.charAt(0).toUpperCase() + nameLowered.slice(1);
		} catch (e) {
			return name;
		}
	}
}
